import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Briefcase, Calendar, Upload, Loader, X, CheckCircle, FileText, Video, AlertCircle, AlertTriangle } from 'lucide-react';
import { MarkdownView } from '../components/MarkdownEditor';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api/careers`;

const MAX_DOC_MB = 25;
const MAX_VIDEO_MB = 1024;
const MAX_DOCS = 5;

const fmtBytes = (n) => {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

export const Careers = () => {
  const [job, setJob] = useState(null);
  const [intro, setIntro] = useState('');
  const [notice, setNotice] = useState({ text: '', visible: false });
  const [loading, setLoading] = useState(true);
  const [showApply, setShowApply] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/job`);
        setJob(data.job);
        setIntro(data.intro || '');
        setNotice({ text: data.notice_text || '', visible: !!data.notice_visible });
      } catch (e) {
        setJob(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen px-4 sm:px-6 lg:px-8 py-16" data-testid="careers-page">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-10">
          <span className="eyebrow" data-testid="careers-eyebrow">Join the team</span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mt-3 mb-4 text-strong" data-testid="careers-heading">
            Available Positions
          </h1>
          <p className="text-muted whitespace-pre-wrap" data-testid="careers-intro">{intro}</p>
        </div>

        {notice.visible && notice.text && !loading && (
          <NoticeBanner text={notice.text} />
        )}

        {loading ? (
          <div className="text-center py-16">
            <Loader className="w-7 h-7 accent-green animate-spin mx-auto" />
          </div>
        ) : !job ? (
          <div className="card-surface p-10 text-center" data-testid="careers-empty">
            <Briefcase className="w-10 h-10 text-faded mx-auto mb-4" />
            <p className="text-default font-medium mb-2">There are no open positions at this time.</p>
            <p className="text-sm text-muted">Please check back later.</p>
          </div>
        ) : submitted ? (
          <SubmittedCard onAnother={() => { setSubmitted(false); setShowApply(true); }} jobTitle={job.title} />
        ) : (
          <div className="card-surface p-6 sm:p-8" data-testid="careers-job-card">
            <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
              <div>
                <h2 className="text-2xl font-semibold text-strong" data-testid="careers-job-title">{job.title}</h2>
                {job.posted_at && (
                  <p className="text-xs text-faded mt-1 inline-flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5" />
                    Posted {new Date(job.posted_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                  </p>
                )}
              </div>
              <button
                onClick={() => setShowApply(true)}
                className="btn-primary px-5 py-2.5 text-sm inline-flex items-center gap-2"
                data-testid="careers-apply-button"
              >
                <Briefcase className="w-4 h-4" />
                Apply now
              </button>
            </div>
            <MarkdownView testid="careers-job-description">{job.description}</MarkdownView>
          </div>
        )}

        {showApply && job && !submitted && (
          <ApplyModal
            jobTitle={job.title}
            onClose={() => setShowApply(false)}
            onSubmitted={() => { setShowApply(false); setSubmitted(true); }}
          />
        )}
      </div>
    </div>
  );
};

const SubmittedCard = ({ onAnother, jobTitle }) => (
  <div className="card-surface p-10 text-center" data-testid="careers-submitted">
    <CheckCircle className="w-12 h-12 accent-green mx-auto mb-5" />
    <h2 className="text-2xl font-semibold text-strong mb-3">Application received</h2>
    <p className="text-muted mb-2">Thanks for applying for <span className="text-strong">{jobTitle}</span>.</p>
    <p className="text-sm text-faded">We&apos;ll review and reach out if you&apos;re a fit. Check your email for a confirmation.</p>
  </div>
);

const NoticeBanner = ({ text }) => (
  <div
    className="mb-8 rounded-lg border p-4 sm:p-5 careers-notice"
    role="alert"
    data-testid="careers-notice"
    style={{
      background:
        'linear-gradient(135deg, rgba(250, 204, 21, 0.10) 0%, rgba(250, 204, 21, 0.04) 100%)',
      borderColor: 'rgba(250, 204, 21, 0.45)',
    }}
  >
    <div className="flex items-start gap-3">
      <div
        className="shrink-0 mt-0.5 w-9 h-9 rounded-full flex items-center justify-center careers-notice-icon"
        style={{
          background: 'rgba(250, 204, 21, 0.18)',
          border: '1px solid rgba(250, 204, 21, 0.55)',
        }}
      >
        <AlertTriangle className="w-5 h-5" style={{ color: '#FACC15' }} />
      </div>
      <div className="flex-1 min-w-0">
        <p
          className="text-xs uppercase tracking-[0.18em] font-semibold mb-1"
          style={{ color: '#FACC15' }}
        >
          Important notice
        </p>
        <div style={{ color: '#fefce8' }}>
          <MarkdownView>{text}</MarkdownView>
        </div>
      </div>
    </div>
  </div>
);

const ApplyModal = ({ jobTitle, onClose, onSubmitted }) => {
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '', location: '',
    message: '', youtube_url: '', consent: false,
    website: '', // honeypot
  });
  const [docs, setDocs] = useState([]);
  const [video, setVideo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');

  const update = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const onPickDocs = (e) => {
    const files = Array.from(e.target.files || []);
    const valid = [];
    for (const f of files) {
      const mb = f.size / 1024 / 1024;
      if (mb > MAX_DOC_MB) {
        setError(`${f.name}: too large (${mb.toFixed(1)} MB). Max is ${MAX_DOC_MB} MB per document.`);
        continue;
      }
      valid.push(f);
    }
    const merged = [...docs, ...valid].slice(0, MAX_DOCS);
    setDocs(merged);
    if (valid.length) setError('');
    e.target.value = '';
  };

  const onPickVideo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const mb = f.size / 1024 / 1024;
    if (mb > MAX_VIDEO_MB) {
      setError(`Video too large (${mb.toFixed(0)} MB). Max is ${MAX_VIDEO_MB} MB. For larger files, paste a YouTube link instead.`);
      e.target.value = '';
      return;
    }
    setVideo(f);
    setError('');
    e.target.value = '';
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.consent) { setError('Please confirm the consent checkbox.'); return; }
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim()) {
      setError('Name and email are required.'); return;
    }

    setSubmitting(true);
    setProgress(0);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, k === 'consent' ? String(v) : v));
      docs.forEach((d) => fd.append('documents', d));
      if (video) fd.append('video', video);

      await axios.post(`${API}/apply`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      onSubmitted();
    } catch (err) {
      setError(err.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-black/75 backdrop-blur-sm overflow-y-auto" data-testid="apply-modal">
      <div className="card-surface max-w-2xl w-full p-6 my-8">
        <div className="flex justify-between items-start mb-5">
          <div>
            <h3 className="text-lg font-semibold text-strong">Apply: {jobTitle}</h3>
            <p className="text-xs text-muted mt-1">Tell us a bit about yourself. Required fields are marked with *.</p>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="apply-close"><X className="w-5 h-5 text-faded" /></button>
        </div>

        <form onSubmit={submit} className="space-y-3" data-testid="apply-form">
          {/* honeypot */}
          <input
            type="text"
            name="website"
            value={form.website}
            onChange={(e) => update('website', e.target.value)}
            tabIndex={-1}
            autoComplete="off"
            style={{ position: 'absolute', left: '-9999px', width: 0, height: 0, opacity: 0 }}
            aria-hidden="true"
          />
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name *" testid="apply-first-name">
              <input value={form.first_name} onChange={(e) => update('first_name', e.target.value)} required className="input-field w-full px-3 py-2 text-sm" />
            </Field>
            <Field label="Last name *" testid="apply-last-name">
              <input value={form.last_name} onChange={(e) => update('last_name', e.target.value)} required className="input-field w-full px-3 py-2 text-sm" />
            </Field>
          </div>
          <Field label="Email *" testid="apply-email">
            <input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} required className="input-field w-full px-3 py-2 text-sm" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Phone" testid="apply-phone">
              <input value={form.phone} onChange={(e) => update('phone', e.target.value)} className="input-field w-full px-3 py-2 text-sm" />
            </Field>
            <Field label="Location" testid="apply-location">
              <input value={form.location} onChange={(e) => update('location', e.target.value)} placeholder="City, State" className="input-field w-full px-3 py-2 text-sm" />
            </Field>
          </div>
          <Field label="Why are you a good fit?" testid="apply-message">
            <textarea value={form.message} onChange={(e) => update('message', e.target.value)} rows={4} className="input-field w-full px-3 py-2 text-sm" placeholder="Brief intro, relevant experience, anything we should know." />
          </Field>

          {/* Documents */}
          <div>
            <label className="block text-xs uppercase tracking-wider text-faded mb-1">
              <FileText className="w-3.5 h-3.5 inline mr-1" />
              Resume, cover letter, references
            </label>
            <input type="file" multiple onChange={onPickDocs} accept=".pdf,.doc,.docx,.txt,.rtf,.odt,.jpg,.jpeg,.png,.heic,.webp" className="block w-full text-sm text-muted file:mr-3 file:py-2 file:px-3 file:rounded file:border file:border-default file:bg-transparent file:text-strong file:text-xs file:cursor-pointer hover:file:border-emphasis" data-testid="apply-docs-input" />
            <p className="text-xs text-faded mt-1">Up to {MAX_DOCS} files · max {MAX_DOC_MB} MB each · PDF / DOC / DOCX / TXT / images</p>
            {docs.length > 0 && (
              <div className="mt-2 space-y-1" data-testid="apply-docs-list">
                {docs.map((d, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 p-2 rounded border border-subtle" style={{ background: 'var(--bg-base)' }}>
                    <span className="text-xs text-strong font-mono truncate">{d.name} <span className="text-faded">({fmtBytes(d.size)})</span></span>
                    <button type="button" onClick={() => setDocs(docs.filter((_, j) => j !== i))} className="text-faded hover:text-red-300"><X className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Video */}
          <div>
            <label className="block text-xs uppercase tracking-wider text-faded mb-1">
              <Video className="w-3.5 h-3.5 inline mr-1" />
              Video (optional)
            </label>
            <input type="file" onChange={onPickVideo} accept="video/mp4,video/quicktime,video/x-m4v,video/webm,video/x-matroska,video/avi,video/x-ms-wmv,.mp4,.mov,.m4v,.webm,.mkv,.avi,.wmv" className="block w-full text-sm text-muted file:mr-3 file:py-2 file:px-3 file:rounded file:border file:border-default file:bg-transparent file:text-strong file:text-xs file:cursor-pointer hover:file:border-emphasis" data-testid="apply-video-input" />
            <p className="text-xs text-faded mt-1">Max {MAX_VIDEO_MB} MB · MP4 / MOV / WebM. For larger videos, use the YouTube field below.</p>
            {video && (
              <div className="mt-2 flex items-center justify-between gap-2 p-2 rounded border border-subtle" style={{ background: 'var(--bg-base)' }} data-testid="apply-video-selected">
                <span className="text-xs text-strong font-mono truncate">{video.name} <span className="text-faded">({fmtBytes(video.size)})</span></span>
                <button type="button" onClick={() => setVideo(null)} className="text-faded hover:text-red-300"><X className="w-4 h-4" /></button>
              </div>
            )}
          </div>

          <Field label={<><Video className="w-3.5 h-3.5 inline mr-1" />YouTube link (optional)</>} testid="apply-youtube">
            <input value={form.youtube_url} onChange={(e) => update('youtube_url', e.target.value)} placeholder="https://youtu.be/…" className="input-field w-full px-3 py-2 text-sm" />
          </Field>

          {/* Consent */}
          <label className="flex items-start gap-2.5 mt-4 cursor-pointer">
            <input type="checkbox" checked={form.consent} onChange={(e) => update('consent', e.target.checked)} className="mt-0.5" data-testid="apply-consent" />
            <span className="text-xs text-muted leading-relaxed">
              I confirm the information I&apos;ve provided is accurate, and consent to LAB21G contacting me about this position by email, phone, or text.
            </span>
          </label>

          {error && (
            <div className="p-3 rounded border border-red-500/40 bg-red-500/10 text-sm text-red-300 inline-flex items-start gap-2" data-testid="apply-error">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {submitting && progress > 0 && progress < 100 && (
            <div className="text-xs text-muted" data-testid="apply-progress">Uploading… {progress}%</div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full btn-primary px-4 py-2.5 text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2"
            data-testid="apply-submit"
          >
            {submitting ? <><Loader className="w-4 h-4 animate-spin" /><span>Submitting…</span></> : <><Upload className="w-4 h-4" /><span>Submit application</span></>}
          </button>
        </form>
      </div>
    </div>
  );
};

const Field = ({ label, children, testid }) => (
  <div data-testid={testid}>
    <label className="block text-xs uppercase tracking-wider text-faded mb-1">{label}</label>
    {children}
  </div>
);

export default Careers;
